type TUnknownRecord = Record<string, unknown>;

type TSignalWireTokenClaimSubset = {
  sub?: string;
  scope?: string;
  resource?: string;
  iat?: number;
  exp?: number;
  jti?: string;
};

type TSignalWireDebugContext = {
  context: string;
  payload?: unknown;
};

type TCreateSignalWireBrowserClientParams = {
  host?: string | null;
  project: string;
  token: string;
};

const SIGNALWIRE_DEBUG_ENABLED =
  (process.env.EXPO_PUBLIC_SIGNALWIRE_DEBUG || "").trim() === "1";
const SIGNALWIRE_USE_STANDARD_V3_AUTH = !["0", "false", "no", "off"].includes(
  (process.env.EXPO_PUBLIC_SIGNALWIRE_USE_STANDARD_V3_AUTH || "")
    .trim()
    .toLowerCase(),
);

const SIGNALWIRE_PATCH_MARKER = "__clinicosJwtProjectPatchApplied";
const SIGNALWIRE_REQUESTER_VALIDATION_RETRY_MARKER =
  "__clinicosRequesterValidationRetried";
let signalWirePatchPromise: Promise<void> | null = null;
const SIGNALWIRE_SESSION_STORAGE_KEYS = [
  "as-SAT",
  "pt-SAT",
  "ci-SAT",
  "as-VRT",
  "pt-VRT",
  "ci-VRT",
] as const;

const isRecord = (value: unknown): value is TUnknownRecord =>
  typeof value === "object" && value !== null;

const toSerializable = (value: unknown): unknown => {
  const seen = new WeakSet<object>();

  try {
    return JSON.parse(
      JSON.stringify(value, (_key, currentValue: unknown) => {
        if (currentValue instanceof Error) {
          const serializedError: TUnknownRecord = {
            name: currentValue.name,
            message: currentValue.message,
            stack: currentValue.stack,
          };
          Object.getOwnPropertyNames(currentValue).forEach((propertyName) => {
            if (!(propertyName in serializedError)) {
              serializedError[propertyName] = (
                currentValue as unknown as TUnknownRecord
              )[propertyName];
            }
          });
          return serializedError;
        }

        if (isRecord(currentValue)) {
          if (seen.has(currentValue)) {
            return "[Circular]";
          }
          seen.add(currentValue);
        }

        return currentValue;
      }),
    );
  } catch {
    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
      };
    }
    return String(value);
  }
};

const getNested = (value: unknown, path: string[]): unknown => {
  let current: unknown = value;
  for (const key of path) {
    if (!isRecord(current) || !(key in current)) {
      return undefined;
    }
    current = current[key];
  }
  return current;
};

const getSignalWireOriginalRequestHops = (errorPayload: unknown): unknown => {
  const candidatePaths = [
    ["data", "original_request", "hops"],
    ["original_request", "hops"],
    ["error", "data", "original_request", "hops"],
    ["cause", "data", "original_request", "hops"],
  ];

  for (const path of candidatePaths) {
    const candidate = getNested(errorPayload, path);
    if (candidate !== undefined) {
      return candidate;
    }
  }
  return undefined;
};

const getSignalWireErrorCode = (errorPayload: unknown): unknown => {
  const candidatePaths = [
    ["code"],
    ["error", "code"],
    ["data", "code"],
    ["cause", "code"],
  ];

  for (const path of candidatePaths) {
    const candidate = getNested(errorPayload, path);
    if (candidate !== undefined) {
      return candidate;
    }
  }
  return undefined;
};

const getSignalWireErrorMessage = (
  errorPayload: unknown,
): string | undefined => {
  const candidatePaths = [
    ["message"],
    ["error", "message"],
    ["data", "message"],
    ["cause", "message"],
  ];

  for (const path of candidatePaths) {
    const candidate = getNested(errorPayload, path);
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate;
    }
  }

  if (typeof errorPayload === "string" && errorPayload.trim().length > 0) {
    return errorPayload;
  }

  return undefined;
};

const base64UrlDecode = (value: string): string => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded =
    normalized.length % 4 === 0
      ? normalized
      : normalized + "=".repeat(4 - (normalized.length % 4));

  if (typeof atob === "function") {
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }

  return "";
};

const decodeSignalWireTokenClaimSubset = (
  token: string,
): TSignalWireTokenClaimSubset | null => {
  try {
    const parts = token.split(".");
    if (parts.length < 2 || !parts[1]) {
      return null;
    }
    const payloadRaw = base64UrlDecode(parts[1]);
    if (!payloadRaw) {
      return null;
    }

    const payload = JSON.parse(payloadRaw) as TUnknownRecord;
    return {
      sub: typeof payload.sub === "string" ? payload.sub : undefined,
      scope: typeof payload.scope === "string" ? payload.scope : undefined,
      resource:
        typeof payload.resource === "string" ? payload.resource : undefined,
      iat: typeof payload.iat === "number" ? payload.iat : undefined,
      exp: typeof payload.exp === "number" ? payload.exp : undefined,
      jti: typeof payload.jti === "string" ? payload.jti : undefined,
    };
  } catch {
    return null;
  }
};

const logSignalWireDebug = ({ context, payload }: TSignalWireDebugContext) => {
  if (!SIGNALWIRE_DEBUG_ENABLED) return;

  if (payload === undefined) {
    console.info(`[SignalWireDebug] ${context}`);
    return;
  }

  console.info(`[SignalWireDebug] ${context}`, payload);
};

const summarizeConnectAuthorization = (
  connectResult: unknown,
): {
  authorization_type?: string;
  has_fabric_subscriber: boolean;
  resource?: string;
  project_id?: string;
  scopes?: unknown;
} => {
  const authorization = getNested(connectResult, ["authorization"]);
  if (!isRecord(authorization)) {
    return {
      has_fabric_subscriber: false,
    };
  }
  return {
    authorization_type:
      typeof authorization.type === "string" ? authorization.type : undefined,
    has_fabric_subscriber: Boolean(isRecord(authorization.fabric_subscriber)),
    resource:
      typeof authorization.resource === "string"
        ? authorization.resource
        : undefined,
    project_id:
      typeof authorization.project_id === "string"
        ? authorization.project_id
        : undefined,
    scopes: authorization.scopes,
  };
};

export const logSignalWireTokenClaimSubset = (token: string): void => {
  if (!SIGNALWIRE_DEBUG_ENABLED) return;

  logSignalWireDebug({
    context: "Minted token claim subset",
    payload: decodeSignalWireTokenClaimSubset(token),
  });
};

export const logSignalWireErrorPayload = (
  context: string,
  error: unknown,
): void => {
  if (!SIGNALWIRE_DEBUG_ENABLED) return;

  const serializedError = toSerializable(error);
  const code = getSignalWireErrorCode(serializedError);
  const message = getSignalWireErrorMessage(serializedError);
  const originalRequestHops = getSignalWireOriginalRequestHops(serializedError);

  logSignalWireDebug({
    context,
    payload: {
      code,
      message,
      original_request_hops: originalRequestHops,
      error: serializedError,
    },
  });
};

export const clearSignalWireSessionStorage = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  const sessionStorageRef = window.sessionStorage;
  if (!sessionStorageRef) {
    return;
  }

  const clearedKeys: string[] = [];
  SIGNALWIRE_SESSION_STORAGE_KEYS.forEach((key) => {
    if (sessionStorageRef.getItem(key) !== null) {
      sessionStorageRef.removeItem(key);
      clearedKeys.push(key);
    }
  });

  if (clearedKeys.length > 0) {
    logSignalWireDebug({
      context: "Cleared stale SignalWire session storage keys",
      payload: { keys: clearedKeys },
    });
  }
};

export const ensureSignalWireConnectIncludesProject =
  async (): Promise<void> => {
    if (signalWirePatchPromise) {
      return signalWirePatchPromise;
    }

    signalWirePatchPromise = (async () => {
      const signalWireCoreModule = await import("@signalwire/core");
      const baseJWTSessionPrototype = signalWireCoreModule.BaseJWTSession
        ?.prototype as unknown as TUnknownRecord | undefined;
      const rpcConnect = signalWireCoreModule.RPCConnect as
        | ((params: unknown) => unknown)
        | undefined;

      if (!baseJWTSessionPrototype || typeof rpcConnect !== "function") {
        return;
      }

      if (baseJWTSessionPrototype[SIGNALWIRE_PATCH_MARKER]) {
        return;
      }

      baseJWTSessionPrototype.authenticate =
        async function patchedAuthenticate() {
          const session = this as {
            options?: {
              project?: string;
              token?: string;
            };
            _connectParams?: TUnknownRecord;
            _relayProtocolIsValid?: () => boolean;
            relayProtocol?: string;
            retrieveRelayProtocol?: () => Promise<string>;
            retrieveSwAuthorizationState?: () => Promise<string>;
            execute?: (message: unknown) => Promise<unknown>;
            persistRelayProtocol?: () => Promise<void>;
            _checkTokenExpiration?: () => Promise<void>;
            removeRelayProtocol?: () => void;
            removeSwAuthorizationState?: () => void;
            removePrevCallId?: () => void;
            logger?: {
              debug?: (...args: unknown[]) => void;
            };
            _rpcConnectResult?: unknown;
          };

          const connectParams = session._connectParams || {};
          const existingAuthentication = isRecord(connectParams.authentication)
            ? connectParams.authentication
            : {};

          const authentication = {
            ...existingAuthentication,
            project: session.options?.project || existingAuthentication.project,
            token: session.options?.token || existingAuthentication.token,
            jwt_token: session.options?.token,
          } as TUnknownRecord;

          const params: TUnknownRecord = {
            ...connectParams,
            authentication,
          };

          if (session._relayProtocolIsValid?.()) {
            params.protocol = session.relayProtocol;
          } else if (session.retrieveRelayProtocol) {
            const previousProtocol = await session.retrieveRelayProtocol();
            if (previousProtocol) {
              params.protocol = previousProtocol;
            }
          }

          if (params.protocol && session.retrieveSwAuthorizationState) {
            const authorizationState =
              await session.retrieveSwAuthorizationState();
            if (authorizationState) {
              params.authorization_state = authorizationState;
            }
          }

          logSignalWireDebug({
            context: "signalwire.connect metadata",
            payload: {
              version: params.version,
              agent: params.agent,
              authentication_project_present: Boolean(
                isRecord(authentication) && authentication.project,
              ),
            },
          });

          try {
            if (!session.execute) {
              throw new Error("SignalWire execute method is unavailable.");
            }
            session._rpcConnectResult = await session.execute(
              rpcConnect(params),
            );
            logSignalWireDebug({
              context: "signalwire.connect authorization summary",
              payload: summarizeConnectAuthorization(session._rpcConnectResult),
            });
            await session.persistRelayProtocol?.();
            await session._checkTokenExpiration?.();
          } catch (error) {
            logSignalWireErrorPayload(
              "signalwire.connect failed during authenticate",
              error,
            );
            session.logger?.debug?.(
              "Patched BaseJWTSession authenticate error",
              error,
            );
            const requesterValidationFailed = Boolean(
              getSignalWireErrorMessage(error)
                ?.toLowerCase()
                .includes("requester validation failed"),
            );
            const sessionWithRetryMarker = session as unknown as TUnknownRecord;
            const hasRetriedRequesterValidation =
              sessionWithRetryMarker[
                SIGNALWIRE_REQUESTER_VALIDATION_RETRY_MARKER
              ] === true;
            if (requesterValidationFailed && !hasRetriedRequesterValidation) {
              sessionWithRetryMarker[
                SIGNALWIRE_REQUESTER_VALIDATION_RETRY_MARKER
              ] = true;
              session.removeRelayProtocol?.();
              session.removeSwAuthorizationState?.();
              session.removePrevCallId?.();
              await (
                session as { authenticate: () => Promise<void> }
              ).authenticate();
              return;
            }
            sessionWithRetryMarker[
              SIGNALWIRE_REQUESTER_VALIDATION_RETRY_MARKER
            ] = false;
            throw error;
          }
        };

      baseJWTSessionPrototype[SIGNALWIRE_PATCH_MARKER] = true;
      logSignalWireDebug({
        context:
          "Applied BaseJWTSession authenticate patch to include project with jwt_token",
      });
    })().catch((error) => {
      signalWirePatchPromise = null;
      throw error;
    });

    return signalWirePatchPromise;
  };

export const createSignalWireBrowserClient = async ({
  host,
  project,
  token,
}: TCreateSignalWireBrowserClientParams): Promise<unknown> => {
  const signalWireModule = (await import("@signalwire/js")) as {
    SignalWire: (params: {
      host?: string;
      project?: string;
      token: string;
    }) => Promise<unknown>;
  };

  if (SIGNALWIRE_USE_STANDARD_V3_AUTH) {
    logSignalWireDebug({
      context:
        "Creating SignalWire client with standard v3 auth path (token only)",
      payload: {
        has_custom_host: Boolean(host),
      },
    });
    return signalWireModule.SignalWire({
      ...(host ? { host } : {}),
      token,
    });
  }

  await ensureSignalWireConnectIncludesProject();
  logSignalWireDebug({
    context:
      "Creating SignalWire client with compatibility auth path (project + token + patch)",
    payload: {
      has_custom_host: Boolean(host),
      has_project: Boolean(project),
    },
  });
  return signalWireModule.SignalWire({
    ...(host ? { host } : {}),
    project,
    token,
  });
};
